import React from "react";
import {Input, InputNumber, Select, Tooltip} from "antd";
import {DeleteOutlined, InfoCircleOutlined} from "@ant-design/icons";
import {paymentContractFeeTypesCompare, daysList2} from "constants/constants";
import config from "_core/config";
import {
    CustomTimeItem,
    CustomTimeItemBody,
    CustomTimeItemActions,
    RemoveButton,
    CompactFormItem,
    CompactLabel
} from "../SubCompanyPaymentContractForm.styles";

const KDV_RATE = config.KDV;

// KDV bilgisi tooltip içeriği oluşturan helper
const getKdvTooltipContent = (amount, isClientAmount = true) => {
    if (!amount || amount === 0) return null;
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return null;

    // Müşteri ücretleri KDV hariç girilir, Kurye ücretleri KDV dahil girilir
    if (isClientAmount) {
        const includeVAT = numAmount * (1 + KDV_RATE);
        return (
            <div>
                <div>KDV Hariç Ücret: {numAmount.toFixed(2)} TL</div>
                <div>KDV Dahil Ücret: {includeVAT.toFixed(2)} TL</div>
                <div style={{marginTop: 4, color: "#faad14"}}>
                    KDV: %{(KDV_RATE * 100).toFixed(0)}
                </div>
            </div>
        );
    } else {
        const excludeVAT = numAmount / (1 + KDV_RATE);
        return (
            <div>
                <div>KDV Dahil Ücret: {numAmount.toFixed(2)} TL</div>
                <div>KDV Hariç Ücret: {excludeVAT.toFixed(2)} TL</div>
                <div style={{marginTop: 4, color: "#faad14"}}>
                    KDV: %{(KDV_RATE * 100).toFixed(0)}
                </div>
            </div>
        );
    }
};

// Saatlik Paket Garanti için tooltip içeriği (form düzenleme modu - hesaplama yapar)
const getPackageGuaranteeTooltipContent = (packageCount, packagePrice, isClientAmount = true) => {
    if (!packageCount || !packagePrice) return null;
    const numCount = parseFloat(packageCount);
    const numPrice = parseFloat(packagePrice);
    if (isNaN(numCount) || isNaN(numPrice)) return null;

    const total = numCount * numPrice;

    // Müşteri ücretleri KDV hariç girilir, Kurye ücretleri KDV dahil girilir
    if (isClientAmount) {
        const totalIncludeVAT = total * (1 + KDV_RATE);
        return (
            <div>
                <div>Paket Sayısı: {numCount}</div>
                <div>Paket Ücreti: {numPrice.toFixed(2)} TL</div>
                <div
                    style={{
                        borderTop: "1px solid rgba(255,255,255,0.3)",
                        marginTop: 4,
                        paddingTop: 4
                    }}
                >
                    <div>Toplam (KDV Hariç): {total.toFixed(2)} TL</div>
                    <div>Toplam (KDV Dahil): {totalIncludeVAT.toFixed(2)} TL</div>
                    <div style={{marginTop: 4, color: "#faad14"}}>
                        KDV: %{(KDV_RATE * 100).toFixed(0)}
                    </div>
                </div>
            </div>
        );
    } else {
        const totalExcludeVAT = total / (1 + KDV_RATE);
        return (
            <div>
                <div>Paket Sayısı: {numCount}</div>
                <div>Paket Ücreti: {numPrice.toFixed(2)} TL</div>
                <div
                    style={{
                        borderTop: "1px solid rgba(255,255,255,0.3)",
                        marginTop: 4,
                        paddingTop: 4
                    }}
                >
                    <div>Toplam (KDV Dahil): {total.toFixed(2)} TL</div>
                    <div>Toplam (KDV Hariç): {totalExcludeVAT.toFixed(2)} TL</div>
                    <div style={{marginTop: 4, color: "#faad14"}}>
                        KDV: %{(KDV_RATE * 100).toFixed(0)}
                    </div>
                </div>
            </div>
        );
    }
};

// Saatlik Paket Garanti için tooltip içeriği (detay modu - BE'den gelen hesaplanmış değer)
const getPackageGuaranteeDetailTooltipContent = (
    packageCount,
    totalAmount,
    isClientAmount = true
) => {
    if (!packageCount || !totalAmount) return null;
    const numCount = parseFloat(packageCount);
    const numTotal = parseFloat(totalAmount);
    if (isNaN(numCount) || isNaN(numTotal)) return null;

    // Müşteri ücretleri KDV hariç, Kurye ücretleri KDV dahil
    if (isClientAmount) {
        const totalIncludeVAT = numTotal * (1 + KDV_RATE);
        return (
            <div>
                <div>Paket Sayısı: {numCount}</div>
                <div
                    style={{
                        borderTop: "1px solid rgba(255,255,255,0.3)",
                        marginTop: 4,
                        paddingTop: 4
                    }}
                >
                    <div>Toplam (KDV Hariç): {numTotal.toFixed(2)} TL</div>
                    <div>Toplam (KDV Dahil): {totalIncludeVAT.toFixed(2)} TL</div>
                    <div>KDV: %{(KDV_RATE * 100).toFixed(0)}</div>
                </div>
            </div>
        );
    } else {
        const totalExcludeVAT = numTotal / (1 + KDV_RATE);
        return (
            <div>
                <div>Paket Sayısı: {numCount}</div>
                <div
                    style={{
                        borderTop: "1px solid rgba(255,255,255,0.3)",
                        marginTop: 4,
                        paddingTop: 4
                    }}
                >
                    <div>Toplam (KDV Dahil): {numTotal.toFixed(2)} TL</div>
                    <div>Toplam (KDV Hariç): {totalExcludeVAT.toFixed(2)} TL</div>
                    <div style={{marginTop: 4, color: "#faad14"}}>
                        KDV: %{(KDV_RATE * 100).toFixed(0)}
                    </div>
                </div>
            </div>
        );
    }
};

export const CustomTimeFeeItem = ({
    feeItem,
    feeType,
    index,
    disabled,
    onRemove,
    onChange,
    userIsCustomer,
    packagePriceInfo = null,
    isEditing = false,
    canDelete = true
}) => {
    const handleFieldChange = (field, value) => {
        onChange(index, field, value);
    };

    const handleOptionsChange = (field, value) => {
        onChange(index, `options.${field}`, value);
    };

    const getSelectedDays = () => {
        const days = feeItem.options?.days || {};
        return Object.keys(days)
            .filter(key => days[key] === true || days[key] === "true")
            .map(key => {
                const day = daysList2.find(d => d.value.toString() === key);
                return day?.name || "";
            })
            .filter(Boolean);
    };

    const selectedDaysText = getSelectedDays().join(", ") || "Gün seçilmedi";

    const isPackageType = feeType === "PAKET";
    const isPackageGuarantee = feeType === "SAATLIK_GARANTI_PAKET";

    return (
        <CustomTimeItem>
            <CustomTimeItemBody>
                {/* SAATLIK_GARANTI_PAKET için Paket Sayısı alanları */}
                {isPackageGuarantee ? (
                    <>
                        <CompactFormItem>
                            <CompactLabel style={{display: "flex", alignItems: "center", gap: 2}}>
                                {userIsCustomer ? "Paket Sayısı:" : "Müşteri Paket Sayısı:"}
                                {isEditing ? (
                                    feeItem.clientGuaranteePackageCount &&
                                    packagePriceInfo?.clientAmount ? (
                                        <Tooltip
                                            title={getPackageGuaranteeTooltipContent(
                                                feeItem.clientGuaranteePackageCount,
                                                packagePriceInfo.clientAmount,
                                                true
                                            )}
                                        >
                                            <InfoCircleOutlined
                                                style={{color: "#1890ff", cursor: "pointer"}}
                                            />
                                        </Tooltip>
                                    ) : (
                                        <InfoCircleOutlined />
                                    )
                                ) : feeItem.clientGuaranteePackageCount &&
                                  !packagePriceInfo &&
                                  feeItem.clientGuaranteePackageAmount ? (
                                    <Tooltip
                                        title={getPackageGuaranteeDetailTooltipContent(
                                            feeItem.clientGuaranteePackageCount,
                                            feeItem.clientGuaranteePackageAmount,
                                            true
                                        )}
                                    >
                                        <InfoCircleOutlined
                                            style={{color: "#1890ff", cursor: "pointer"}}
                                        />
                                    </Tooltip>
                                ) : (
                                    <InfoCircleOutlined />
                                )}
                                {/* Form modunda hesaplama yapan tooltip */}
                            </CompactLabel>
                            <InputNumber
                                className="payment-contract-form-input"
                                size="small"
                                value={feeItem.clientGuaranteePackageCount}
                                onChange={value =>
                                    handleFieldChange(
                                        "clientGuaranteePackageCount",
                                        Math.abs(value)
                                    )
                                }
                                disabled={disabled}
                                min={0}
                                style={{width: 80}}
                            />
                        </CompactFormItem>

                        {!userIsCustomer && (
                            <CompactFormItem>
                                <CompactLabel
                                    style={{display: "flex", alignItems: "center", gap: 2}}
                                >
                                    Kurye Paket Sayısı:
                                    {/* Form modunda hesaplama yapan tooltip */}
                                    {isEditing ? (
                                        feeItem.courierGuaranteePackageCount &&
                                        packagePriceInfo?.courierAmount ? (
                                            <Tooltip
                                                title={getPackageGuaranteeTooltipContent(
                                                    feeItem.courierGuaranteePackageCount,
                                                    packagePriceInfo.courierAmount,
                                                    false
                                                )}
                                            >
                                                <InfoCircleOutlined
                                                    style={{color: "#1890ff", cursor: "pointer"}}
                                                />
                                            </Tooltip>
                                        ) : (
                                            <InfoCircleOutlined />
                                        )
                                    ) : feeItem.courierGuaranteePackageCount &&
                                      !packagePriceInfo &&
                                      feeItem.courierGuaranteePackageAmount ? (
                                        <Tooltip
                                            title={getPackageGuaranteeDetailTooltipContent(
                                                feeItem.courierGuaranteePackageCount,
                                                feeItem.courierGuaranteePackageAmount,
                                                false
                                            )}
                                        >
                                            <InfoCircleOutlined
                                                style={{color: "#1890ff", cursor: "pointer"}}
                                            />
                                        </Tooltip>
                                    ) : (
                                        <InfoCircleOutlined />
                                    )}
                                </CompactLabel>
                                <InputNumber
                                    className="payment-contract-form-input"
                                    size="small"
                                    value={feeItem.courierGuaranteePackageCount}
                                    onChange={value =>
                                        handleFieldChange(
                                            "courierGuaranteePackageCount",
                                            Math.abs(value)
                                        )
                                    }
                                    disabled={disabled}
                                    min={0}
                                    style={{width: 80}}
                                />
                            </CompactFormItem>
                        )}
                    </>
                ) : (
                    <>
                        {/* Standart Müşteri/Kurye Ücreti alanları */}
                        <CompactFormItem>
                            <CompactLabel style={{display: "flex", alignItems: "center", gap: 2}}>
                                {userIsCustomer ? "Ücret:" : "Müşteri Ücreti:"}
                                {feeItem.clientAmount ? (
                                    <Tooltip
                                        title={getKdvTooltipContent(feeItem.clientAmount, true)}
                                    >
                                        <InfoCircleOutlined
                                            style={{color: "#1890ff", cursor: "pointer"}}
                                        />
                                    </Tooltip>
                                ) : (
                                    <InfoCircleOutlined />
                                )}
                            </CompactLabel>
                            <InputNumber
                                className="payment-contract-form-input"
                                type="number"
                                size="small"
                                value={feeItem.clientAmount}
                                onChange={e => handleFieldChange("clientAmount", Math.abs(e))}
                                disabled={disabled}
                                min={0}
                                style={{width: 80}}
                            />
                        </CompactFormItem>

                        {!userIsCustomer && (
                            <CompactFormItem>
                                <CompactLabel
                                    style={{display: "flex", alignItems: "center", gap: 2}}
                                >
                                    Kurye Ücreti:
                                    {feeItem.courierAmount ? (
                                        <Tooltip
                                            title={getKdvTooltipContent(
                                                feeItem.courierAmount,
                                                false
                                            )}
                                        >
                                            <InfoCircleOutlined
                                                style={{color: "#1890ff", cursor: "pointer"}}
                                            />
                                        </Tooltip>
                                    ) : (
                                        <InfoCircleOutlined />
                                    )}
                                </CompactLabel>
                                <InputNumber
                                    className="payment-contract-form-input"
                                    type="number"
                                    size="small"
                                    value={feeItem.courierAmount}
                                    onChange={e => handleFieldChange("courierAmount", Math.abs(e))}
                                    disabled={disabled}
                                    min={0}
                                    style={{width: 70}}
                                />
                            </CompactFormItem>
                        )}
                    </>
                )}

                {/* PAKET tipi için Paket Limiti alanı (Kademe) */}
                {isPackageType && (
                    <CompactFormItem>
                        <CompactLabel>Paket Limiti:</CompactLabel>
                        <InputNumber
                            className="payment-contract-form-input"
                            type="number"
                            size="small"
                            value={feeItem.options?.packageLimit}
                            onChange={e => handleOptionsChange("packageLimit", Math.abs(e))}
                            disabled={disabled}
                            min={0}
                            style={{width: 70}}
                        />
                    </CompactFormItem>
                )}

                {!userIsCustomer && feeType === "KM" && (
                    <CompactFormItem>
                        <CompactLabel>Kurye Minimum Mesafe (km):</CompactLabel>
                        <InputNumber
                            className="payment-contract-form-input"
                            type="number"
                            size="small"
                            value={feeItem.courierMinimumDistance}
                            onChange={e => handleFieldChange("courierMinimumDistance", Math.abs(e))}
                            disabled={disabled}
                            min={0}
                            suffix="km"
                            style={{width: 70}}
                        />
                    </CompactFormItem>
                )}

                {feeType === "KM" && (
                    <CompactFormItem>
                        <CompactLabel>Müşteri Minimum Mesafe (km):</CompactLabel>
                        <InputNumber
                            className="payment-contract-form-input"
                            type="number"
                            size="small"
                            value={feeItem.clientMinimumDistance}
                            onChange={e => handleFieldChange("clientMinimumDistance", Math.abs(e))}
                            disabled={disabled}
                            min={0}
                            suffix="km"
                            style={{width: 70}}
                        />
                    </CompactFormItem>
                )}

                {isPackageGuarantee && (
                    <CompactFormItem style={{maxWidth: "100%", display: "flex", flexWrap: "wrap"}}>
                        <CompactLabel>Karşılaştırılacak Ücret Tipleri:</CompactLabel>
                        <Select
                            className="payment-contract-form-input"
                            mode="multiple"
                            size="small"
                            value={feeItem.options?.feeTypesCompare || []}
                            onChange={value => handleOptionsChange("feeTypesCompare", value)}
                            disabled={disabled}
                            placeholder="Seç"
                            style={{width: 240}}
                            maxTagCount={4}
                        >
                            {paymentContractFeeTypesCompare.map(type => (
                                <Select.Option key={type.value} value={type.value}>
                                    {type.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </CompactFormItem>
                )}

                {feeType === "BONUS_PAKET_KAZANCI_UCRETI" && (
                    <CompactFormItem>
                        <CompactLabel>Minimum Paket Adedi:</CompactLabel>
                        <InputNumber
                            className="payment-contract-form-input"
                            type="number"
                            size="small"
                            value={feeItem.options?.minPackageCount}
                            onChange={e => handleOptionsChange("minPackageCount", Math.abs(e))}
                            disabled={disabled}
                            min={0}
                            style={{width: 60}}
                        />
                    </CompactFormItem>
                )}

                {/* Günler seçimi */}
                <CompactFormItem style={{maxWidth: "100%"}}>
                    <CompactLabel>Günler:</CompactLabel>
                    <Tooltip title={selectedDaysText}>
                        <Select
                            className="payment-contract-form-input"
                            mode="multiple"
                            size="small"
                            value={getSelectedDays()}
                            onChange={selectedDays => {
                                const newDays = {
                                    "0": false,
                                    "1": false,
                                    "2": false,
                                    "3": false,
                                    "4": false,
                                    "5": false,
                                    "6": false
                                };
                                selectedDays.forEach(dayName => {
                                    const day = daysList2.find(d => d.name === dayName);
                                    if (day) {
                                        newDays[day.value.toString()] = true;
                                    }
                                });
                                handleOptionsChange("days", newDays);
                            }}
                            disabled={disabled}
                            placeholder="Seç"
                            style={{minWidth: "70px", maxWidth: "100%"}}
                        >
                            {daysList2.map(day => (
                                <Select.Option key={day.value} value={day.name}>
                                    {day.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Tooltip>
                </CompactFormItem>
            </CustomTimeItemBody>

            <CustomTimeItemActions>
                {!disabled && canDelete && (
                    <Tooltip title="Sil">
                        <RemoveButton onClick={() => onRemove(index)}>
                            <DeleteOutlined style={{fontSize: 12}} />
                        </RemoveButton>
                    </Tooltip>
                )}
            </CustomTimeItemActions>
        </CustomTimeItem>
    );
};
